import React, { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import { useCart } from "../../components/CartContext";

import "./css/CartScreen.css";


function getImageUrl(image) {
  if (!image) {
    return "";
  }

  if (
    image.startsWith("http://") ||
    image.startsWith("https://")
  ) {
    return image;
  }

  return `${image}`;
}


function CartScreen() {
  const {
    restaurantId,
    tableId,
  } = useParams();

  const navigate = useNavigate();

  const {
    restaurantName,
  } = useSelector(
    (state) => state.theme
  );

  const {
    cart = [],
    increaseQuantity,
    decreaseQuantity,
    removeFromCart,
    clearCart,
    cartTotal = 0,
  } = useCart();


  const itemCount = useMemo(() => {
    return cart.reduce(
      (total, item) =>
        total + Number(item.quantity || 0),
      0
    );
  }, [cart]);


  const formatPrice = (value) => {
    return `₹${Number(value || 0).toFixed(2)}`;
  };


  const handleContinueOrdering = () => {
    navigate(
      `/${restaurantId}/table/${tableId}`
    );
  };


  const handleCheckout = () => {
    if (!cart.length) {
      return;
    }

    navigate(
      `/${restaurantId}/table/${tableId}/checkout`
    );
  };


  if (!cart.length) {

    return (

      <main className="theme4-cart-page">

        <section className="theme4-cart-empty">

          <div className="theme4-cart-empty-inner">

            <span className="theme4-cart-eyebrow">
              YOUR ORDER
            </span>

            <div className="theme4-cart-empty-line">
            </div>

            <h1>
              Your cart is
              <br />
              <em>empty.</em>
            </h1>

            <p>
              Discover our menu and
              select something exceptional
              for your table.
            </p>

            <button
              type="button"
              className="theme4-cart-gold-button"
              onClick={handleContinueOrdering}
            >
              EXPLORE MENU
              <span>→</span>
            </button>

          </div>

        </section>

      </main>

    );
  }


  return (

    <main className="theme4-cart-page">

      {/* =====================================================
          CART HERO
          ===================================================== */}

      <section className="theme4-cart-header">

        <div className="theme4-cart-container">

          <div className="theme4-cart-header-label">

            <span>
              01
            </span>

            <div>
            </div>

            <span>
              YOUR ORDER
            </span>

          </div>


          <div className="theme4-cart-title-row">

            <div>

              <span className="theme4-cart-eyebrow">
                {restaurantName ||
                  "RESTAURANT"}
              </span>

              <h1>
                Your
                <br />
                <em>Selection.</em>
              </h1>

            </div>


            <div className="theme4-cart-count">

              <span>
                ITEMS
              </span>

              <strong>
                {String(itemCount).padStart(2, "0")}
              </strong>

            </div>

          </div>

        </div>

      </section>


      {/* =====================================================
          CART CONTENT
          ===================================================== */}

      <section className="theme4-cart-content">

        <div className="theme4-cart-container">

          <div className="theme4-cart-layout">


            {/* =================================================
                ITEMS
                ================================================= */}

            <div className="theme4-cart-items">

              <div className="theme4-cart-items-heading">

                <span>
                  YOUR SELECTION
                </span>

                <span>
                  {itemCount}{" "}
                  {itemCount === 1
                    ? "ITEM"
                    : "ITEMS"}
                </span>

              </div>


              {cart.map(
                (item, index) => {

                  const quantity =
                    Number(
                      item.quantity || 1
                    );

                  const itemPrice =
                    Number(
                      item.price || 0
                    );

                  const itemTotal =
                    itemPrice * quantity;


                  return (

                    <article
                      key={
                        item.id ||
                        `${item.name}-${index}`
                      }
                      className="theme4-cart-item"
                    >

                      {/* IMAGE */}

                      <div className="theme4-cart-item-image">

                        {getImageUrl(
                          item.image ||
                          item.image_url ||
                          item.photo
                        ) ? (

                          <img
                            src={getImageUrl(
                              item.image ||
                              item.image_url ||
                              item.photo
                            )}
                            alt={
                              item.name ||
                              "Menu item"
                            }
                          />

                        ) : (

                          <div className="theme4-cart-no-image">
                            NO IMAGE
                          </div>

                        )}

                        <span>
                          {String(
                            item.display_order ||
                            index + 1
                          ).padStart(2, "0")}
                        </span>

                      </div>


                      {/* DETAILS */}

                      <div className="theme4-cart-item-details">

                        <div className="theme4-cart-item-top">

                          <div>

                            <span className="theme4-cart-item-category">
                              {item.categoryName ||
                                item.category_name ||
                                "SELECTION"}
                            </span>

                            <h2>
                              {item.name}
                            </h2>

                          </div>


                          <button
                            type="button"
                            className="theme4-cart-remove"
                            onClick={() =>
                              removeFromCart(
                                item.id
                              )
                            }
                            aria-label={`Remove ${item.name}`}
                          >
                            ×
                          </button>

                        </div>


                        {item.description && (

                          <p className="theme4-cart-item-description">
                            {item.description}
                          </p>

                        )}


                        <div className="theme4-cart-item-bottom">

                          {/* QUANTITY */}

                          <div className="theme4-cart-quantity">

                            <button
                              type="button"
                              onClick={() =>
                                decreaseQuantity(
                                  item.id
                                )
                              }
                              aria-label="Decrease quantity"
                            >
                              −
                            </button>

                            <span>
                              {quantity}
                            </span>

                            <button
                              type="button"
                              onClick={() =>
                                increaseQuantity(
                                  item.id
                                )
                              }
                              aria-label="Increase quantity"
                            >
                              +
                            </button>

                          </div>


                          {/* PRICE */}

                          <div className="theme4-cart-item-price">

                            <span>
                              {formatPrice(
                                itemPrice
                              )}{" "}
                              each
                            </span>

                            <strong>
                              {formatPrice(
                                itemTotal
                              )}
                            </strong>

                          </div>

                        </div>

                      </div>

                    </article>

                  );

                }
              )}


              {/* CLEAR CART */}

              <button
                type="button"
                className="theme4-cart-clear"
                onClick={clearCart}
              >
                CLEAR ORDER
              </button>

            </div>


            {/* =================================================
                SUMMARY
                ================================================= */}

            <aside className="theme4-cart-summary">

              <div className="theme4-cart-summary-inner">

                <span className="theme4-cart-summary-eyebrow">
                  ORDER SUMMARY
                </span>


                <h2>
                  Almost
                  <br />
                  <em>there.</em>
                </h2>


                <div className="theme4-cart-summary-line">
                </div>


                <div className="theme4-cart-summary-row">

                  <span>
                    Items
                  </span>

                  <strong>
                    {itemCount}
                  </strong>

                </div>


                <div className="theme4-cart-summary-row">

                  <span>
                    Subtotal
                  </span>

                  <strong>
                    {formatPrice(
                      cartTotal
                    )}
                  </strong>

                </div>


                <div className="theme4-cart-summary-note">

                  <span>
                    TOTAL
                  </span>

                  <strong>
                    {formatPrice(
                      cartTotal
                    )}
                  </strong>

                </div>


                <button
                  type="button"
                  className="theme4-cart-checkout"
                  onClick={handleCheckout}
                >

                  <span>
                    CONTINUE TO CHECKOUT
                  </span>

                  <strong>
                    →
                  </strong>

                </button>


                <button
                  type="button"
                  className="theme4-cart-continue"
                  onClick={handleContinueOrdering}
                >
                  ← CONTINUE ORDERING
                </button>


                <p className="theme4-cart-table">

                  TABLE{" "}
                  <strong>
                    {tableId}
                  </strong>

                </p>

              </div>

            </aside>

          </div>

        </div>

      </section>

    </main>

  );
}


export default CartScreen;